import type { QueryClient } from '@tanstack/react-query'
import { BANKING_KEYS, CASH_POSITION_SUMMARIES_KEY } from '@/lib/query-keys'

/**
 * Invalidate every cache that the removal of manual bank accounts can change,
 * whether one account goes (`useDeleteBankAccount`) or a whole manual bank
 * (`useDeleteBankInstitution`). One list so the two deletes cannot drift.
 *
 * - bank accounts: the Settings lists and every account picker;
 * - Balance Import Status: a join of `bank_accounts` and `balances`;
 * - Cash Position Summary and the balance KPI tiles: the removed accounts no
 *   longer contribute their balances;
 * - Credit Card Availability: manual cards are listed there.
 *
 * @param queryClient - The active query client
 */
export function invalidateManualAccountRemoval(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBankAccounts })
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBalanceImportStatus })
  queryClient.invalidateQueries({ queryKey: CASH_POSITION_SUMMARIES_KEY })
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allKpi })
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allCreditCardAvailability })
}

/**
 * Invalidate every cache that shows a manual bank's `institution_name` after
 * the bank is renamed (`useRenameBankInstitution`), so the new name appears
 * wherever the institution does — not only after a reload.
 *
 * - bank accounts: Settings lists, pickers and the manual-transaction form;
 * - Balance Import Status: shows the bank name per account;
 * - Cash Position Summary: kept as before the rename helper existed;
 * - transaction lists that embed the account (`bank_accounts(institution_name, …)`):
 *   Daily Banking (legacy `['plaid', 'transactions']` key, with its Category
 *   options under `['plaid', 'categories']`, as `useUpdateBankAccount` does),
 *   Manual Transactions (+ counts) and a file import's transactions.
 *
 * Credit Card Availability and LOC transactions are not here: their RPCs
 * return no institution name. A rename changes no balance, so no KPI either.
 *
 * @param queryClient - The active query client
 */
export function invalidateAfterInstitutionRename(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBankAccounts })
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBalanceImportStatus })
  queryClient.invalidateQueries({ queryKey: CASH_POSITION_SUMMARIES_KEY })
  queryClient.invalidateQueries({ queryKey: ['plaid', 'transactions'] })
  queryClient.invalidateQueries({ queryKey: ['plaid', 'categories'] })
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allManualTransactions })
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allManualTransactionCounts })
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allFileImportTransactions })
}
